class ScHeaderMenu extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector(".sc-menu-wrapper");
    this.toggleButtons = this.querySelectorAll("[sc-toggle-menu]");
    this.submenuLinks = this.querySelectorAll("[toggle-submenu]");

    this.toggleButtons.forEach((el) => {
      el.addEventListener("click", () => {
        if (this.wrapper) {
          this.wrapper.classList.toggle("is-open");
        }
      });
    });

    this.submenuLinks.forEach((el) => {
      el.addEventListener("click", (e) => {
        const linkContainer = el.closest(".sc-menu-link");
        const sub = linkContainer.nextElementSibling;

        if (!sub || !sub.classList.contains("sc-menu-sublinks")) return;

        const isOpen = sub.dataset.sublinkState === "1";

        // First click opens submenu; second click navigates to parent URL
        if (isOpen) return;

        e.preventDefault();
        sub.dataset.sublinkState = "1";
        sub.classList.add("menu-sublinks-open");
        sub.style.height = `${sub.scrollHeight}px`;
      });
    });
  }
}

customElements.define("sc-header-menu", ScHeaderMenu)

/* SC FAQ Toggle Button */

class QuestionToggle extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <span class="sc-icon plus"></span>
      <span class="sc-icon minus"></span>
    `;
  }

  connectedCallback() {
    this.faqBlock = this.closest(".faq-block");
    this.faqBlock.addEventListener("click", () => this.toggle());
  }

  toggle() {
    const isOpen = this.classList.contains("open");
    this.classList.toggle("open", !isOpen);
    this.faqBlock.classList.toggle("active", !isOpen);
  }
}

customElements.define("question-toggle", QuestionToggle);


/* SC Product Page */
class QuantityButton extends HTMLElement {
  connectedCallback() {
    const row = this.closest('.product-quantity-quantity');
    if (!row) return;

    this.input = row.querySelector('.quantity-input');
    if (!this.input) return;

    this.syncState();

    // Every listener is registered once, even if this element is connected again
    // later. Stacking them would make a single press step the value as many
    // times as the element had been reconnected.
    if (this.bound) return;
    this.bound = true;

    this.addEventListener('click', () => this.handleClick());

    // The element carries role="button" but is not a real <button>, so the keys
    // a button would handle for free have to be handled here.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      this.handleClick();
    });

    this.input.addEventListener('change', () => this.syncState());
  }

  handleClick() {
    const type = this.getAttribute('type');
    let currentValue = parseInt(this.input.value) || 1;
    const maxQuantity = parseInt(this.input.getAttribute('max')) || Infinity;

    if (type === 'plus' && currentValue < maxQuantity) {
      this.input.value = currentValue + 1;
    } else if (type === 'minus' && currentValue > 1) {
      this.input.value = currentValue - 1;
    }

    this.input.dispatchEvent(new Event('change'));
  }

  // Greys out the step that would do nothing, so the limit is visible rather
  // than the press just being silently ignored.
  syncState() {
    const type = this.getAttribute('type');
    const currentValue = parseInt(this.input.value) || 1;
    const maxQuantity = parseInt(this.input.getAttribute('max')) || Infinity;

    const atLimit = type === 'plus' ? currentValue >= maxQuantity : currentValue <= 1;

    this.classList.toggle('is-disabled', atLimit);
    this.setAttribute('aria-disabled', String(atLimit));
  }
}

class AddToCartButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.addToCart);

    // DATORACODE - Autonomes Custom Element hat keine Button-Semantik: Tastatur nachruesten.
    // Native Reihenfolge: Enter loest bei keydown aus, die Leertaste erst bei keyup. Gedrueckt
    // gehaltene Tasten duerfen nicht mehrfach in den Warenkorb legen.
    this.addEventListener('keydown', (event) => {
      if (event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault(); // nur das Scrollen unterbinden, ausgeloest wird bei keyup
        return;
      }
      if (event.key !== 'Enter' || event.repeat) return;
      event.preventDefault();
      this.addToCart();
    });

    this.addEventListener('keyup', (event) => {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      this.addToCart();
    });
  }

  connectedCallback() {
    this.quantityInput = this.closest('.sc-product-mash')?.querySelector('.quantity-input')
      || document.querySelector('.quantity-input');
    this.successText = this.getAttribute('data-success');

    // DATORACODE - Rolle und Tabstopp, damit Tastatur und Screenreader den Button erreichen
    if (!this.hasAttribute('role')) this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    this.setAttribute('aria-disabled', this.hasAttribute('disable') ? 'true' : 'false');
  }

  async addToCart() {
    if (this.classList.contains('sc-load') || this.hasAttribute('disable')) return;

    // DATORACODE - Label erst beim Klick lesen, damit ein Variantenwechsel es nicht überschreibt
    const originalText = this.querySelector('span')?.textContent;

    if (!this.quantityInput) {
      console.error('Quantity input not found');
      return;
    }

    this.classList.add('sc-load');
    const variantId = this.getAttribute('data-variant-id');
    const quantity = parseInt(this.quantityInput.value, 10) || 1;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    try {
      // Collect section IDs (cart-drawer etc.) so Shopify returns updated HTML
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.message || 'Failed to add to cart');
      }

      // Refresh full cart state, then attach rendered sections for line items
      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent,
          },
        })
      );

      this.querySelector('span').textContent = this.successText;
      setTimeout(() => {
        this.querySelector('span').textContent = originalText;
      }, 8000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
      this.querySelector('span').textContent = 'Error';
      setTimeout(() => {
        this.querySelector('span').textContent = originalText;
      }, 2000);
    } finally {
      this.classList.remove('sc-load');
    }
  }
}

customElements.define('button-quantity', QuantityButton);
customElements.define('button-add-cart', AddToCartButton);

/* SC Product variant picker – one group per product option */
class ScVariantPicker extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-sc-variants]');
    if (!script) return;

    try {
      this.variants = JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse product variants', error);
      return;
    }

    // DATORACODE - Auf den eigenen Produkt-Container scopen, damit PDP und Quick-Buy-Modal sich nicht gegenseitig steuern
    const scope = this.closest('[data-sc-product-root]') || document;

    this.addToCartBtn = scope.querySelector('button-add-cart');
    this.quantityInput = scope.querySelector('.quantity-input');
    this.priceEl = scope.querySelector('.sc-product-price_price');
    this.gallery = scope.querySelector('[data-sc-product-gallery]');
    // DATORACODE - Quick-Buy-Modal hat keine Swiper-Galerie, das Bild wird direkt getauscht
    this.quickBuyImages = Array.from(scope.querySelectorAll('[data-sc-quick-buy-image]'));

    // DATORACODE - Im Modal darf die Variante NICHT in die Adresszeile geschrieben werden,
    // sonst bekommt die Startseite ein ?variant= der angeklickten Karte
    this.isQuickBuy = this.closest('[data-sc-quick-buy]') !== null;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sc-option-value]');
      if (!button || button.hasAttribute('disable') || button.classList.contains('active')) return;

      const group = button.closest('[data-sc-option-index]');
      if (!group) return;

      group.querySelectorAll('[data-sc-option-value]').forEach((el) => {
        el.classList.remove('active');
        el.setAttribute('aria-pressed', 'false');
      });
      button.classList.add('active');
      button.setAttribute('aria-pressed', 'true');

      const selectedLabel = group.querySelector('[data-sc-selected-value]');
      if (selectedLabel) selectedLabel.textContent = button.dataset.scOptionValue;

      this.updateVariant();
    });

    this.updateAvailability(this.getSelectedOptions());
    this.syncGallery();
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-sc-option-index]')).map((group) => {
      const active = group.querySelector('.heat-selector.active');
      return active ? active.dataset.scOptionValue : null;
    });
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) =>
      selectedOptions.every((value, index) => !value || variant.options[index] === value)
    );
  }

  getVariantMediaId(variant) {
    if (!variant) return null;
    return variant.featured_image?.id || variant.featured_media?.id || null;
  }

  syncGallery(variant = this.findVariant(this.getSelectedOptions())) {
    const mediaId = this.getVariantMediaId(variant);
    if (!mediaId || !this.gallery?.scGoToMedia) return;
    this.gallery.scGoToMedia(mediaId);
  }

  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);
    if (!variant) return;

    if (this.addToCartBtn) {
      this.addToCartBtn.setAttribute('data-variant-id', String(variant.id));
      if (variant.available) {
        this.addToCartBtn.removeAttribute('disable');
      } else {
        this.addToCartBtn.setAttribute('disable', '');
      }
      // DATORACODE - aria-disabled mitfuehren, das Custom Element kennt kein natives disabled
      this.addToCartBtn.setAttribute('aria-disabled', variant.available ? 'false' : 'true');

      // DATORACODE - Button-Text folgt der Verfügbarkeit, wenn die Labels gesetzt sind
      const label = variant.available
        ? this.addToCartBtn.dataset.addLabel
        : this.addToCartBtn.dataset.soldOutLabel;
      const labelEl = this.addToCartBtn.querySelector('span');
      if (label && labelEl) labelEl.textContent = label;
    }

    if (this.quantityInput && typeof variant.inventory_quantity === 'number') {
      // Mirrors the Liquid rule: a cap only applies to tracked stock that refuses
      // oversell. Anything else stays uncapped, otherwise a variant sold beyond
      // its counter gets limited to whatever that counter happens to say.
      const capped = Boolean(variant.inventory_management) && variant.inventory_policy === 'deny';

      if (capped) {
        const maxQty = Math.max(variant.inventory_quantity, 1);
        this.quantityInput.setAttribute('max', String(maxQty));

        if (parseInt(this.quantityInput.value, 10) > maxQty) {
          this.quantityInput.value = String(maxQty);
        }
      } else {
        this.quantityInput.removeAttribute('max');
      }

      // The quantity buttons derive their disabled state from max and only
      // recompute on change. Without this the plus button stays greyed out (and
      // announced as disabled) after switching to a better stocked variant.
      this.quantityInput.dispatchEvent(new Event('change'));
    }

    if (this.priceEl && typeof variant.price === 'number') {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }

    if (!this.isQuickBuy) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      history.replaceState({}, '', url.toString());
    }

    this.updateQuickBuyImages(variant);
    this.updateAvailability(selectedOptions);
    this.syncGallery(variant);
  }

  // DATORACODE - Variantenbild im Quick-Buy-Modal nachziehen
  updateQuickBuyImages(variant) {
    if (!this.quickBuyImages.length) return;

    var src = variant.featured_image && variant.featured_image.src;
    if (!src) return;

    var base = src.split(/[?&]width=/)[0];
    var separator = base.indexOf('?') === -1 ? '?' : '&';

    this.quickBuyImages.forEach(function (wrapper) {
      var img = wrapper.querySelector('img');
      if (!img) return;

      // srcset wuerde src ueberstimmen
      img.removeAttribute('srcset');
      img.src = base + separator + 'width=' + (wrapper.dataset.scQuickBuyImage || '800');
    });
  }

  updateAvailability(selectedOptions) {
    this.querySelectorAll('[data-sc-option-index]').forEach((group, optionIndex) => {
      group.querySelectorAll('[data-sc-option-value]').forEach((button) => {
        const value = button.dataset.scOptionValue;
        const isAvailable = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[optionIndex] !== value) return false;

          return selectedOptions.every((selectedValue, index) => {
            if (index === optionIndex || !selectedValue) return true;
            return variant.options[index] === selectedValue;
          });
        });

        // "disable" is this theme's own marker and means nothing to assistive
        // tech: native disabled keeps sold-out options out of the tab order,
        // aria-disabled matches the state rendered in Liquid.
        if (isAvailable) {
          button.removeAttribute('disable');
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
        } else {
          button.setAttribute('disable', '');
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
      });
    });
  }

  formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'CHF';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
        style: 'currency',
        currency,
      }).format(Number(amount));
    } catch (error) {
      return `${currency} ${amount}`;
    }
  }
}

customElements.define('sc-variant-picker', ScVariantPicker);

class HideOpenFAQ extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <svg id="Pfeil" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
        <path id="Pfad_37" data-name="Pfad 37" d="M6214.8,1384.942l4.8-4.8,4.8,4.8" transform="translate(-6204.304 -1368.442)" fill="none" stroke="#2e2c27" stroke-width="2"/>
      </svg>
    `;
  }

  connectedCallback() {
    this.faqBlock = this.closest(".faq-block");
    this.faqBlock.addEventListener("click", () => this.toggle());
  }

  toggle() {
    const isOpen = this.classList.contains("open");
    this.classList.toggle("open", !isOpen);
    this.faqBlock.classList.toggle("active", !isOpen);
  }
}

customElements.define("faq-toggle", HideOpenFAQ);

class SizeChartModal extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
      const linkText = this.getAttribute('link-text') || 'Größentabelle';
      // Passed in from Liquid so the label follows the storefront language.
      const closeLabel = this.getAttribute('close-label') || 'Schliessen';
      // Read from the template when there is one: its content never renders, so
      // the chart cannot appear on screen before this element upgrades.
      const template = this.querySelector('template');
      const pageContent = template ? template.innerHTML : this.innerHTML;

      // getAttribute hands back the decoded value, so Liquid's escape is already
      // undone. A title containing a quote would otherwise break out of the
      // attribute it is interpolated into below.
      const linkTextAttr = linkText.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

      // A ruler reads as "measurements" at a glance and gives the link a target
      // bigger than the text alone.
      const icon = this.hasAttribute('with-icon')
        ? `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
             <rect x="1.4" y="7" width="21.2" height="10" rx="1.4" stroke="currentColor" stroke-width="1.6"/>
             <path d="M6 7v3.4M10 7v2.2M14 7v3.4M18 7v2.2" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
           </svg>`
        : '';

      // The table comes from a Shopify page, so it arrives as bare markup with no
      // classes. Shadow styles reach it because it is rendered inside this root,
      // which is also why the page stylesheet cannot be used here.
      this.shadowRoot.innerHTML = `
        <style>
          :host { display: block; }

          .link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 0;
            border: 0;
            background: none;
            font: inherit;
            color: inherit;
            cursor: pointer;
          }

          .link .label {
            text-decoration: underline;
            text-underline-offset: 3px;
          }

          /* em keeps the ruler locked to the link text at any size. */
          .link svg {
            flex: 0 0 auto;
            width: 1em;
            height: 1em;
          }

          .link:focus-visible {
            outline: 2px solid currentColor;
            outline-offset: 3px;
          }

          .modal {
            position: fixed;
            inset: 0;
            display: none;
            align-items: center;
            justify-content: center;
            padding: 20px;
            background: rgba(46, 44, 39, 0.55);
            z-index: 9999;
          }

          .modal.active { display: flex; }

          .modal-content {
            position: relative;
            display: flex;
            flex-direction: column;
            width: 100%;
            max-width: 560px;
            max-height: 85vh;
            background: #fff;
            border-radius: 2px;
            box-shadow: 0 24px 60px rgba(46, 44, 39, 0.28);
            animation: pop 0.22s cubic-bezier(0.4, 0, 0.2, 1);
          }

          @keyframes pop {
            from { opacity: 0; transform: translateY(8px); }
            to   { opacity: 1; transform: none; }
          }

          @media (prefers-reduced-motion: reduce) {
            .modal-content { animation: none; }
          }

          header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 16px;
            padding: 22px 26px;
            border-bottom: 1px solid #eee8dd;
          }

          h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            line-height: 1.2;
            color: #2e2c27;
          }

          .close {
            flex: 0 0 auto;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            padding: 0;
            border: 0;
            border-radius: 50%;
            background: #faf6ef;
            color: #2e2c27;
            cursor: pointer;
            transition: background 0.2s ease;
          }

          .close:hover { background: #eee8dd; }

          .close:focus-visible {
            outline: 2px solid #2e2c27;
            outline-offset: 2px;
          }

          .close svg { display: block; width: 14px; height: 14px; }

          /* Horizontal scroll keeps a wide table usable on a phone instead of
             squeezing the columns until the numbers wrap. */
          .content {
            overflow: auto;
            padding: 6px 26px 26px;
          }

          .content table {
            width: 100%;
            border-collapse: collapse;
            font-size: 15px;
            color: #2e2c27;
          }

          .content th,
          .content td {
            padding: 11px 14px;
            text-align: right;
            white-space: nowrap;
            border-bottom: 1px solid #eee8dd;
          }

          .content th:first-child,
          .content td:first-child {
            text-align: left;
            font-weight: 700;
          }

          .content thead th,
          .content tr:first-child th {
            position: sticky;
            top: 0;
            background: #faf6ef;
            font-weight: 700;
            border-bottom: 1px solid #e4ded3;
          }

          .content tbody tr:last-child td { border-bottom: 0; }
          .content tbody tr:hover td { background: #faf6ef; }

          .content p { margin: 0 0 12px; font-size: 14px; line-height: 1.55; }
          .content p:last-child { margin-bottom: 0; }

          @media (max-width: 600px) {
            header { padding: 18px 20px; }
            .content { padding: 6px 20px 20px; }
            .content table { font-size: 14px; }
            .content th, .content td { padding: 9px 10px; }
          }
        </style>

        <button type="button" class="link">
          ${icon}<span class="label">${linkText}</span>
        </button>

        <div class="modal" role="dialog" aria-modal="true" aria-label="${linkTextAttr}">
          <div class="modal-content">
            <header>
              <h2>${linkText}</h2>
              <button type="button" class="close" aria-label="${closeLabel}">
                <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
            </header>
            <div class="content">${pageContent}</div>
          </div>
        </div>
      `;

      this.modal = this.shadowRoot.querySelector('.modal');
      this.closeButton = this.shadowRoot.querySelector('.close');
      const link = this.shadowRoot.querySelector('.link');

      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      link.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.close());
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.close();
      });
    }

    open() {
      this.opener = this.shadowRoot.activeElement || document.activeElement;
      this.modal.classList.add('active');
      // Locked on <html>, not <body>: custom.css.liquid sets overflow-x on html,
      // so body's overflow no longer propagates to the viewport and locking it
      // there would do nothing.
      // Counted, because two dialogs open at once would otherwise have the first
      // one to close restore scrolling, and the last one write the lock back in
      // and leave the page stuck.
      const root = document.documentElement;
      const open = Number(root.dataset.scModalOpen || 0);
      if (open === 0) root.dataset.scModalPreviousOverflow = root.style.overflow;
      root.dataset.scModalOpen = String(open + 1);
      root.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      this.closeButton.focus();
    }

    close() {
      this.modal.classList.remove('active');

      const root = document.documentElement;
      const open = Math.max(Number(root.dataset.scModalOpen || 1) - 1, 0);
      root.dataset.scModalOpen = String(open);
      if (open === 0) root.style.overflow = root.dataset.scModalPreviousOverflow || '';
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }
  }

  customElements.define('size-chart-modal', SizeChartModal);

/* DATORACODE - CS-13 Sticky Add-to-Cart auf der PDP.
   Der Haupt-Button in sc-product bleibt die einzige Quelle: Variante, Verfuegbarkeit
   und Preis werden von dort gespiegelt, der Variantenwaehler muss den Sticky nicht kennen. */
class ScStickyAtc extends HTMLElement {
  connectedCallback() {
    const section = this.closest('.sc-section') || document;
    this.mainBtn = section.querySelector('.sc-product-info button-add-cart');
    this.stickyBtn = this.querySelector('button-add-cart');
    if (!this.mainBtn || !this.stickyBtn) return;

    const target = this.mainBtn.closest('.sc-product-mash') || this.mainBtn;
    this.mainPrice = section.querySelector('.sc-product-info .sc-product-price_price');
    this.stickyPrice = this.querySelector('[data-sc-sticky-price]');
    this.mainQty = target.querySelector('.quantity-input');
    this.stickyQty = this.querySelector('[data-sc-sticky-quantity]');

    // Optional: gewaehlte Variante mit Bild und Optionen (Einstellung je Geraet)
    this.optionsEl = this.querySelector('[data-sc-sticky-options]');
    this.imageEl = this.querySelector('[data-sc-sticky-image] img');
    const variantsScript = this.querySelector('[data-sc-sticky-variants]');
    if (variantsScript) {
      try {
        this.variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.error('Failed to parse sticky variants', error);
      }
    }

    this.syncButton();
    this.syncPrice();
    this.syncQuantity(this.mainQty, this.stickyQty);

    this.mutationObserver = new MutationObserver(() => {
      this.syncButton();
      this.syncPrice();
      this.syncQuantity(this.mainQty, this.stickyQty);
    });
    this.mutationObserver.observe(this.mainBtn, {
      attributes: true,
      attributeFilter: ['data-variant-id', 'disable', 'aria-disabled'],
    });
    if (this.mainPrice) {
      this.mutationObserver.observe(this.mainPrice, { childList: true, characterData: true, subtree: true });
    }

    // Beide Mengenfelder bleiben gleich, egal wo gezaehlt wird. Der Sticky-Button liest,
    // wie jeder <button-add-cart> ausserhalb des Kaufbereichs, das Haupt-Mengenfeld.
    if (this.mainQty && this.stickyQty) {
      // Der Variantenwaehler setzt max und kuerzt den Wert ohne Event
      this.mutationObserver.observe(this.mainQty, { attributes: true, attributeFilter: ['max'] });
      ['input', 'change'].forEach((type) => {
        this.mainQty.addEventListener(type, () => this.syncQuantity(this.mainQty, this.stickyQty));
        this.stickyQty.addEventListener(type, () => this.syncQuantity(this.stickyQty, this.mainQty));
      });
    }

    // Mobile: sichtbar, solange der Kaufbereich nicht im Bild ist, also schon beim Laden,
    // wenn er noch unterhalb liegt. Desktop: erst, wenn er nach oben rausgescrollt ist.
    this.target = target;
    this.mobileQuery = window.matchMedia('(max-width: 886px)');
    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.targetInView = entry.isIntersecting;
      this.updateVisibility();
      if (!this.classList.contains('is-ready')) {
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-ready')));
      }
    });
    this.intersectionObserver.observe(target);
    this.onBreakpointChange = () => this.updateVisibility();
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);

    // Der Observer meldet sich nur beim Ein- und Austreten. Springt die Seite in einem
    // Schritt ueber den Kaufbereich (Anker, Ende-Taste, wiederhergestellte Scrollposition),
    // aendert sich oben/unten ohne Meldung, deshalb beim Scrollen nachpruefen.
    this.onScroll = () => {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateVisibility();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.bindChatWidget();
  }

  disconnectedCallback() {
    this.mutationObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
    window.removeEventListener('scroll', this.onScroll);
    document.documentElement.classList.remove('sc-sticky-atc-visible');
  }

  updateVisibility() {
    if (this.targetInView === undefined) return;
    if (this.targetInView || this.mobileQuery.matches) {
      this.setVisible(!this.targetInView);
      return;
    }
    this.setVisible(this.target.getBoundingClientRect().top < 0);
  }

  setVisible(visible) {
    if (this.classList.contains('is-visible') === visible && this.hasAttribute('inert') !== visible) return;
    this.applyVisible(visible);
  }

  applyVisible(visible) {
    this.classList.toggle('is-visible', visible);
    // Ausgeblendet weder fokussierbar noch fuer Screenreader vorhanden
    this.toggleAttribute('inert', !visible);

    // Chat-Blase (Tidio) ueber die Leiste heben, siehe sc-sticky-atc.liquid
    const root = document.documentElement;
    if (visible) root.style.setProperty('--sc-sticky-atc-height', `${this.offsetHeight}px`);
    root.classList.toggle('sc-sticky-atc-visible', visible);
  }

  // Das offene Chatfenster ist auf Mobile bildschirmfuellend und darf nicht mit
  // hochgeschoben werden, sonst ist sein Kopf abgeschnitten
  bindChatWidget() {
    const bind = () => {
      if (!window.tidioChatApi || this.chatBound) return;
      this.chatBound = true;
      const root = document.documentElement;
      window.tidioChatApi.on('open', () => root.classList.add('sc-chat-open'));
      window.tidioChatApi.on('close', () => root.classList.remove('sc-chat-open'));
      // Tidio stellt einen offenen Chat beim Seitenwechsel wieder her, ohne 'open' zu
      // melden. Geschlossen ist das iframe nur so gross wie die Blase.
      const iframe = document.getElementById('tidio-chat-iframe');
      if (iframe && iframe.getBoundingClientRect().height > 200) root.classList.add('sc-chat-open');
    };
    if (window.tidioChatApi) bind();
    else document.addEventListener('tidioChat-ready', bind, { once: true });
  }

  syncButton() {
    const variantId = this.mainBtn.getAttribute('data-variant-id');
    if (variantId !== this.stickyBtn.getAttribute('data-variant-id')) this.syncVariant(variantId);
    this.stickyBtn.setAttribute('data-variant-id', variantId);
    this.stickyBtn.toggleAttribute('disable', this.mainBtn.hasAttribute('disable'));
    if (this.stickyBtn.hasAttribute('aria-disabled')) {
      this.stickyBtn.setAttribute('aria-disabled', this.mainBtn.hasAttribute('disable') ? 'true' : 'false');
    }
  }

  syncVariant(variantId) {
    if (!this.variants || !this.optionsEl) return;
    const variant = this.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) return;

    this.optionsEl.replaceChildren(...variant.options.map((value) => {
      const line = document.createElement('span');
      line.textContent = value;
      return line;
    }));

    if (this.imageEl && variant.image) {
      this.imageEl.removeAttribute('srcset');
      this.imageEl.src = variant.image;
    }
  }

  syncQuantity(from, to) {
    if (!from || !to) return;
    // Auch das Entfernen spiegeln: Varianten ohne Bestandsgrenze haben kein max
    const max = from.getAttribute('max');
    const maxChanged = to.getAttribute('max') !== max;
    if (maxChanged) {
      if (max === null) to.removeAttribute('max');
      else to.setAttribute('max', max);
    }
    if (to.value === from.value && !maxChanged) return;
    to.value = from.value;
    // Damit die +/- Buttons des anderen Felds ihren Zustand nachziehen
    to.dispatchEvent(new Event('change'));
  }

  syncPrice() {
    if (!this.mainPrice || !this.stickyPrice) return;
    this.stickyPrice.textContent = this.mainPrice.textContent.trim();
  }
}

customElements.define('sc-sticky-atc', ScStickyAtc);
